import React, { ReactElement, useState } from "react";
import {
  ResourceLine,
  resourceLines,
} from "app/utils/request/scheduling/facilityResources";
import { RootState } from "typedefs";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { EventResponse, LessonSchedule } from "app/models/EventModel";
import { schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import SaveBar from "app/routes/Sessions/SaveBar";
import PagePanel from "app/components/PagePanel";
import InfoModal from "app/routes/Calendar/InfoModal";
import { resetBookSession } from "app/actions/scheduling/bookSession";

import styles from "./styles.module.scss";

import { BookSessionState } from "app/reducers/scheduling/bookSession";
import { cls } from "app/utils";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";

type SummaryItemProps = {
  label: string | ReactElement;
  value: string | ReactElement;
};

const SummaryItem = (props: SummaryItemProps) => {
  const { label, value } = props;
  return (
    <div className={styles.summary_item}>
      <div className={styles.summary_attribute}>{label}</div>
      <div className={styles.summary_value}>{value}</div>
    </div>
  );
};

export default function SessionConfirmation() {
  const history = useHistory();
  const dispatch = useDispatch();
  const bookSession = useSelector(
    (state: RootState): BookSessionState => state.bookSession,
  );
  const selectedSession = useSelector(
    (state: RootState): LessonSchedule => state.bookSession.selectedSession,
  );
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [sessionNotes, setSessionNotes] = useState(selectedSession.notes);
  const [errors, setErrors] = useState([]);
  const [updatedResourceLines, setUpdatedResourceLines] = useState<ResourceLine[]>([]);
  const { t } = useTranslation();

  if (!bookSession.coach) {
    history.push("/sessions/new");
    return null;
  }

  const editSession = () => {
    setLoading(true);
    const payload = {
      start: DateTime.fromMillis(bookSession.time.startHour)
        .setZone(bookSession.coach.timeZone)
        .toISO(),
      end: DateTime.fromMillis(bookSession.time.endHour)
        .setZone(bookSession.coach.timeZone)
        .toISO(),
      notes: sessionNotes,
    };

    schedulingFetchPatch<EventResponse>(
      `/bookings/${selectedSession.id}`,
      payload,
    )
      .then((response) => {
        // The service picks the bay for the new time, so the popup names the
        // bay from the answer, not the bay the session held before.
        setUpdatedResourceLines(resourceLines(response));
        setShowPopup(true);
        setLoading(false);
      })
      .catch((errs) => {
        setLoading(false);
        setErrors(errs.errors);
      });
  };

  const dateStr = DateTime.fromMillis(bookSession.time.startHour)
    .setZone(bookSession.time.timeZone)
    .toLocaleString({ year: "numeric", day: "numeric", month: "long" });

  const startTimeString = DateTime.fromMillis(bookSession.time.startHour)
    .setZone(bookSession.time.timeZone)
    .toLocaleString(DateTime.TIME_SIMPLE);

  const endTimeString = DateTime.fromMillis(bookSession.time.endHour)
    .setZone(bookSession.time.timeZone)
    .toLocaleString(DateTime.TIME_SIMPLE);

  const timeZoneShortString = DateTime.fromMillis(bookSession.time.endHour)
    .setZone(bookSession.time.timeZone)
    .toFormat("ZZZZ");

  const intervalTimeString = `${startTimeString} - ${endTimeString} ${timeZoneShortString}`;

  const currencySymbol = generateCurrencySymbol(selectedSession?.rateCurrency)
    ?? bookSession.coach.currencySymbol;

  const rate = bookSession?.rate ?? bookSession.pricingValue.rate;


  let oldSessionInfo = null;
  if (selectedSession) {
    const prevDateStr = selectedSession.date.toLocaleString({ year: "numeric", day: "numeric", month: "long" });

    const lessonRateId = selectedSession.lessonRateId;
    const oldLessonRate = bookSession.coach.lessonRates.find(
      (lr) => lessonRateId === lr.id,
    );
    const oldBayLines = resourceLines(selectedSession);

    oldSessionInfo = (
      <div className={`${styles.summary_box}`}>
        <h2 className={cls(styles.section_title, styles.edit_section_title)}>
          {t("Old Session")}
        </h2>
        <hr />
        <SummaryItem label={t("Session")} value={oldLessonRate?.title || "-"} />
        <SummaryItem
          label={t("Coach")}
          value={
            <>
              {selectedSession.coachProfile.firstName +
                " " +
                selectedSession.coachProfile.lastName}
            </>
          }
        />
        <SummaryItem label={t("Facility")} value={bookSession.facility.name} />
        <hr />
        <SummaryItem
          label={
            <>
              <span className={styles.summary_icon}>
                <i className="ico ico-calendar" />
              </span>
              {t("Date")}
            </>
          }
          value={prevDateStr}
        />
        <SummaryItem
          label={
            <>
              <span className={styles.summary_icon}>
                <i className="ico ico-clock" />
              </span>
              {t("Time")}
            </>
          }
          value={selectedSession.intervalTimeString}
        />
        {selectedSession.location && (
          <SummaryItem
            label={
              <>
                <span className={styles.summary_icon}>
                  <i className="ico ico-map-marker" />
                </span>
                {t("Location")}
              </>
            }
            value={selectedSession.location}
          />
        )}
        {/* One row for each section, as on the checkout: the section title
            is the label and the bay titles are the value ("Bays | Bay 1"). */}
        {oldBayLines.map((line) => (
          <SummaryItem
            key={line.section}
            label={
              <>
                <span className={styles.summary_icon}>
                  <i className="ico ico-map-marker" />
                </span>
                {line.section || t("Resource")}
              </>
            }
            value={line.titles}
          />
        ))}
        <hr />
        {/* CN-2711: read the flag off the session, not off oldLessonRate —
            that lookup only searches the coach's own options, so a facility
            booking option would fall through and quote the rate anyway. */}
        {!selectedSession.lessonRateHidePrice && (
          <SummaryItem
            label={t("Session Rate")}
            value={`${generateCurrencySymbol(selectedSession?.rateCurrency) || selectedSession.coachProfile.currencySymbol}${selectedSession.rate}`}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.confirmation_page_container} session-confirmation-page-container`}
    >
      <PagePanel
        title={t("Edit Session")}
        buttonType="outline-secondary"
        buttonTitle={<i className="ico ico-back" style={{ color: "#000" }} />}
        url="/sessions/new"
        panelClassName={styles.page_panel}
      />

      <div className={styles.container}>
        <div className={styles.summary_container}>
          <div className={`${styles.summary_box} ${styles.old_session_box}`}>
            <h2
              className={cls(styles.section_title, styles.edit_section_title)}
            >
              {t("Session Summary")}
            </h2>
            <hr />
            <SummaryItem
              label={t("Session")}
              value={bookSession?.lessonRateTitle || '-'}
            />
            <SummaryItem
              label={t("Coach")}
              value={
                <>
                  <img
                    className={styles.avatar}
                    src={bookSession.coach.profilePicture}
                  />
                  {bookSession.coach.fullName}
                </>
              }
            />
            <SummaryItem
              label={t("Facility")}
              value={bookSession.facility.name}
            />
            <hr />
            <SummaryItem
              label={
                <>
                  <span className={styles.summary_icon}>
                    <i className="ico ico-calendar" />
                  </span>
                  {t("Date")}
                </>
              }
              value={dateStr}
            />
            <SummaryItem
              label={
                <>
                  <span className={styles.summary_icon}>
                    <i className="ico ico-clock" />
                  </span>
                  {t("Time")}
                </>
              }
              value={intervalTimeString}
            />
            {bookSession.pricingValue.location && (
              <SummaryItem
                label={
                  <>
                    <span className={styles.summary_icon}>
                      <i className="ico ico-map-marker" />
                    </span>
                    {t("Location")}
                  </>
                }
                value={bookSession.pricingValue.location}
              />
            )}
            <hr />
            {!bookSession.pricingValue?.hidePrice && (
              <SummaryItem
                label={t("Session Rate")}
                value={`${currencySymbol}${rate}`}
              />
            )}
          </div>

          {oldSessionInfo}

          <div className={styles.edit_notes_container}>
            <h2 className={styles.section_title}>{t("Notes")}</h2>
            <textarea
              className={styles.notes_input}
              placeholder={t("Add note here")}
              onChange={(ev) => {
                setSessionNotes(ev.target.value);
              }}
              value={sessionNotes}
            />
          </div>
        </div>
        <SaveBar
          submitLabel={loading ? t("Saving...") : t("Confirm")}
          cancelLabel="Back"
          style={cls(styles.save_bar, styles.confirm_btn)}
          onCancel={() => history.push("/sessions/edit")}
          onSubmit={editSession}
          submitDisabled={loading}
        />
      </div>

      <InfoModal
        isOpen={showPopup}
        title={t("Session updated")}
        status="success"
        onDismiss={() => {
          dispatch(resetBookSession());
          history.push("/sessions");
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{t("You have updated your scheduled session")}</div>
          {updatedResourceLines.map((line) => (
            <div data-testid="updated-resource-row" key={line.section}>
              {line.section ? <>{line.section}: </> : null}
              <b>{line.titles}</b>
            </div>
          ))}
        </div>
      </InfoModal>

      <InfoModal
        isOpen={errors.length > 0}
        title={errors[0]?.details[0]?.message}
        status="error"
        onDismiss={() => {
          history.goBack();
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{errors[0]?.details[0]?.detail}</div>
        </div>
      </InfoModal>
    </div>
  );
}
